/**
 * The panel you get by clicking a building you already placed.
 *
 * Before this, a tap on a finished house, hut or torch did precisely nothing: the
 * sprite swallowed the click (stopPropagation) and then fell through every branch —
 * rush only existed for scaffolding, demolish only while build mode happened to be
 * armed, and "use" only for the three crafting stations. So the most obvious
 * gesture in the game, clicking your own building, was a dead click on almost
 * everything you can build.
 *
 * Now it selects the building and offers what you can actually do with it: use it,
 * hurry it along, MOVE it, or tear it down. Demolition keeps its confirm dialog
 * unchanged — it is still the one irreversible, unrefunded action in the game.
 */
import { el, toast } from '../../dom';
import { api } from '../../../core/network/api';
import type { Building } from '../../game-api';
import { TENT_KIND } from './placement';
import { richText } from '../../ui/icons';
import { playSfx } from '../../audio/audio';
import { BUILDINGS, STATION_KINDS, closePlanter, liveRemaining, openOverlay, setEnergyStatus, type FarmCtx } from './context';
import { isUnderConstruction, openBuildRush, fmtCountdown } from './construction';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { ApiError } from '../../../core/network/api';
import { fieldApi, type EnergyStatus } from '../../game-api';
import { modalWrap } from '../../ui/panel';
import { confirmDemolish, demolish } from './demolish';
import { paintPlots } from './plots';
import { paintChrome, openCrafting, doSleep } from './hud';
import { openSmelting } from './smelting';
import { openForagerShop, type ManageHooks } from './forager-shop';
import { isPenBuilding, openHusbandry } from './husbandry';
import { villageApi } from '../../game-api';
import { KIND_SPRITE, OCCUPIABLE_KINDS, occupantsOf, type Occupant } from './village';

/** Arm placement mode with an EXISTING building, so its ghost follows the cursor. */
export function startMove(c: FarmCtx, b: Building): void {
  closePlanter(c);
  // `buildMode` carries the kind (the ghost, the tile bracket and the validity
  // check all read it), `moving` marks it as a relocation — which exempts the
  // building's own tiles from the occupancy test and routes the commit to
  // field/move rather than field/build. See placement.ts and input.moveBuilding.
  c.buildMode = b.kind;
  c.moving = { tx: b.tx, ty: b.ty, kind: b.kind };
  paintChrome(c);
  toast(`Moving ${BUILDINGS[b.kind]?.name ?? b.kind} — tap a green tile (Build to cancel)`);
}

// ─── The tent ────────────────────────────────────────────────────────────────
// Not a Building row (see placement.ts / the server's game/field/tent.ts), so it
// gets its own tiny client here rather than an entry in game-api's fieldApi.

export interface TentStatus {
  owned: boolean;
  pitched: boolean;
  tx: number | null;
  ty: number | null;
  safeRadius: number;
  lastTx: number | null;
  lastTy: number | null;
  canMove: boolean;
}

export const tentApi = {
  get: () => api.get<TentStatus>('/field/tent'),
  collect: () => api.post<TentStatus>('/field/tent/collect'),
  place: (tx: number, ty: number) => api.post<TentStatus>('/field/tent/place', { tx, ty }),
};

/**
 * Arm placement mode with the PACKED tent, so its ghost follows the cursor.
 *
 * Deliberately shaped like startMove(): `buildMode` carries the kind and the same
 * ghost/validity path runs, because "may this stand here" must have exactly one
 * answer. `moving` stays null — the tent occupies no building row, so there are no
 * old tiles to exempt from the occupancy test.
 */
export function startTentPlacement(c: FarmCtx): void {
  closePlanter(c);
  c.buildMode = TENT_KIND;
  c.moving = null;
  paintChrome(c);
  toast('Pitching the tent — tap a green tile (Build to cancel)');
}

/**
 * The tent panel: what it is for, and what you can do at it — turn in for the
 * night, or pack it up and pitch it back down somewhere else.
 *
 * The safe radius is read from the SERVER's answer rather than a constant here:
 * it is a balance number, and a client that quietly disagreed with it would draw
 * a ring the mobs do not honour.
 */
export function openTentMenu(c: FarmCtx, status: TentStatus): void {
  // min-h-44px: these are the tent's only controls and they are tapped with a thumb.
  const row = (label: string, cls: string, onClick: () => void) =>
    el('button', `w-full px-3 py-2 mb-2 min-h-[44px] rounded border text-sm ${cls}`, { onClick }, label);

  const card = el('div', 'bg-surface border border-border rounded-lg p-4 w-[300px] max-w-[90vw] shadow-lift sheet-card', {},
    el('div', 'flex items-center gap-3 mb-3', {},
      // props/, not buildings/ — the tent is not a Building row and has no baked
      // building thumbnail; /sprites/buildings/tent.png does not exist and rendered
      // as a broken image in the one panel the whole feature is reached through.
      el('img', 'w-10 h-10 object-contain', { src: '/sprites/props/tent_small.png', style: 'image-rendering:pixelated' }),
      el('div', 'flex-1', {},
        el('h3', 'font-heading text-gold', {}, 'Your Tent'),
        el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1', {},
          ...richText(status.pitched
            ? `safe within ${status.safeRadius} tiles of ${status.tx}, ${status.ty}`
            : 'packed in your bag — nowhere is safe')))));

  card.append(el('p', 'text-xs text-text-dim mb-3', {},
    'Nothing will come after you while you stand inside the camp. Turn in for the night to rest up, or pack the tent and pitch it wherever you want your safe ground to be.'));

  if (!status.canMove) {
    // The server refuses either gesture for 15s after a hit; saying so beats a 400.
    // Resting is held to the SAME lock, and deliberately so: the ask was for a way
    // to leave the map so mobs stop hitting you, which is exactly the escape hatch
    // the combat lock exists to deny mid-fight. The lock is not repealed here, and
    // it is not extended in time either — the same 15s, one more gesture.
    card.append(el('p', 'text-xs text-error mb-2', {}, 'Not while something is still after you.'));
  } else if (status.pitched) {
    // Rest = the existing sleep (server: POST /field/sleep, via hud.doSleep). One
    // rest path in the game, not two: it is what refills energy and HP, and its
    // rules are the server's — a home lets you sleep at any hour, without one it
    // must be night, and there is one sleep per world day. This row only ASKS;
    // a refusal comes back as the server's own sentence in a toast. Nothing about
    // those rules is loosened by the tent having a button for it.
    card.append(row('Rest in the tent 💤', 'border-shroom text-shroom hover:bg-shroom/10',
      // Same confirm as the house: the tent's rest IS the sleep, four hours and all.
      () => openSleepPanel(c)));
    card.append(row('Pack it up', 'border-gold text-gold hover:bg-gold/10', () => {
      closePlanter(c);
      // The returned status REPLACES the cached one and the world is rebuilt: the
      // mob loop reads `c.tent` every frame to decide whether the hero is safe, so
      // dropping the answer on the floor here left a packed tent still standing on
      // the map and still turning mobs away from ground that is no longer safe.
      void tentApi.collect()
        .then(async (t) => { c.tent = t; await c.load(); toast('Tent packed — tap Pitch to set it down again'); })
        .catch((e: unknown) => toast(e instanceof Error ? e.message : 'Could not pack the tent'));
    }));
  } else {
    card.append(row('Pitch it here', 'border-gold text-gold hover:bg-gold/10', () => startTentPlacement(c)));
  }

  card.append(el('button', 'w-full px-3 py-2 rounded border border-border text-text-dim hover:text-text text-sm',
    { onClick: () => closePlanter(c) }, 'Close'));

  const wrap = el('div', 'fixed inset-0 z-[65] flex items-center justify-center',
    { onClick: (e) => { if (e.target === wrap) closePlanter(c); } }, card);
  playSfx('ui_open');
  openOverlay(c, wrap);
}


// ─── Sleeping in the house ───────────────────────────────────────────────────
/**
 * Sleeping is no longer a free instant full bar: the server puts the hero INSIDE
 * the building for four hours (field.config SLEEP_DURATION_MS) and refuses farm
 * work until they are up. Which means the client owes the player two things it
 * did not have to show before — how long is left, and how to get out.
 *
 * Two exits, both on the overlay below: pay Shrooms for the hours remaining (the
 * price falls with the clock, so it is re-read every second, not quoted once), or
 * get up for free and forfeit the rest. The free one is why nobody with an empty
 * Shroom balance can be stuck in his own house.
 */

/** Is the server telling us he is asleep? Absent field = awake (older server). */
export const isSleeping = (en: EnergyStatus | null | undefined): boolean => en?.sleeping === true;

/**
 * Remaining sleep, interpolated between polls exactly like every other countdown
 * in this view: the snapshot's number minus the time since it was fetched.
 */
export const sleepLeftMs = (en: EnergyStatus | null | undefined, sinceFetchMs: number): number =>
  Math.max(0, (en?.sleepEndsInMs ?? 0) - Math.max(0, sinceFetchMs));

/** Live skip price from the live remaining time — mirrors home.math.sleepSkipCostShrooms. */
export const sleepSkipPrice = (leftMs: number): number =>
  leftMs > 0 ? Math.max(5, Math.ceil(Math.ceil(leftMs / 60_000) * 0.5)) : 0;

let sleepOverlay: HTMLElement | null = null;
let sleepTimer: number | null = null;

/**
 * Take the sleep screen down and stop its clock.
 *
 * Exported because the overlay outlives the view it belongs to: it hangs off
 * document.body and re-ticks once a second, so signing out mid-sleep left it
 * running on top of the website. Removing the node is only half of it — the
 * interval has to go too, or a signed-out tab keeps waking up to recompute a
 * countdown for a session that ended.
 */
export function closeSleepOverlay(): void {
  sleepOverlay?.remove();
  sleepOverlay = null;
  if (sleepTimer !== null) { clearInterval(sleepTimer); sleepTimer = null; }
}

async function wake(c: FarmCtx, skip: boolean): Promise<void> {
  try {
    await dispatchAction('field.wake', async (id) => {
      const st = await fieldApi.wake(skip, id);
      setEnergyStatus(c, st);
      closeSleepOverlay();
      await c.load();               // the farm is playable again; refill every bar
      await c.game.refreshHud();    // Shrooms may have left the wallet
      toast(skip ? `Slept it off 💤 — full energy · 🍄 ${st.spent}` : 'Up early — you got no rest out of it');
    });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not get up', 'error');
  }
}

/**
 * Show / hide / re-tick the "you are asleep" screen from the latest energy
 * snapshot. Called from paintChrome, which already runs on every load and every
 * action response — so the overlay appears on reload too, not just right after
 * the click that started the sleep. Without that a player who refreshed mid-sleep
 * saw a farm that silently refused every action.
 */
export function syncSleepOverlay(c: FarmCtx): void {
  const en = c.energyStatus;
  if (!isSleeping(en)) { closeSleepOverlay(); return; }
  if (sleepOverlay) return;   // already up; its own timer keeps it current

  const fetchedAt = c.energyStatusAt ?? Date.now();
  const left = () => sleepLeftMs(en, Date.now() - fetchedAt);
  const clock = el('div', 'font-mono text-2xl text-gold mb-2', {}, fmtCountdown(left()));
  const skipBtn = el('button', 'w-full px-3 py-2 mb-2 min-h-[44px] rounded bg-shroom/20 border border-shroom text-shroom hover:bg-shroom/30', {
    onClick: () => void wake(c, true),
  }, '');
  const paintSkip = () => skipBtn.replaceChildren(
    el('span', 'inline-flex items-center justify-center gap-1', {},
      ...richText(`Sleep it off now · 🍄 ${sleepSkipPrice(left())}`)));
  paintSkip();

  const card = el('div', 'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[90vw] shadow-lift text-center sheet-card game-font', {},
    el('h3', 'font-heading text-gold mb-1', {}, 'Fast asleep'),
    el('p', 'text-sm text-text-dim mb-3', {}, 'You are inside, out cold. The farm can wait.'),
    clock,
    el('p', 'text-[11px] text-text-dim mb-4', {}, 'A full night gives you a full bar, your health back and every rest charge.'),
    skipBtn,
    // The free exit is always rendered, never conditioned on anything: it is the
    // one thing that guarantees a player cannot be trapped for four hours.
    el('button', 'w-full px-3 py-2 min-h-[44px] rounded border border-border text-text-dim hover:text-text text-sm',
      { onClick: () => void wake(c, false) }, 'Get up early (no rest)'));

  sleepOverlay = modalWrap(() => undefined, card, 'z-[80]');
  document.body.appendChild(sleepOverlay);
  playSfx('ui_open');
  // 1 Hz, because BOTH numbers move: the countdown and the price it is quoting.
  sleepTimer = window.setInterval(() => {
    const ms = left();
    clock.textContent = fmtCountdown(ms);
    paintSkip();
    if (ms <= 0) {
      // Our interpolation says the night is over — but the server decides, exactly
      // as with build completion. Ask, do not promote.
      closeSleepOverlay();
      void c.load().then(() => c.game.refreshHud()).catch(() => undefined);
    }
  }, 1000);
}

/**
 * The hut's "sleep" row. A confirm step, not a straight POST: four hours is a
 * real commitment and the old button said "Sleep · full energy", which is now a
 * promise the click does not keep on its own.
 */
export function openSleepPanel(c: FarmCtx): void {
  const hours = Math.round((c.energyStatus?.sleepDurationMs ?? 4 * 3_600_000) / 3_600_000);
  const card = el('div', 'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[90vw] shadow-lift text-center sheet-card game-font', {},
    el('h3', 'font-heading text-gold mb-1', {}, 'Turn in for the night'),
    el('p', 'text-sm text-text-dim mb-4', {},
      `You will be inside for ${hours} hours and the farm waits. Wake to a full bar, your health back and every rest charge — or pay Shrooms to sleep it off in one go.`),
    el('button', 'w-full px-3 py-2 mb-2 min-h-[44px] rounded bg-gold/20 border border-gold text-gold hover:bg-gold/30',
      { onClick: () => { closePlanter(c); void doSleep(c); } }, `Sleep ${hours}h 💤`),
    el('button', 'w-full px-3 py-2 min-h-[44px] rounded border border-border text-text-dim hover:text-text text-sm',
      { onClick: () => closePlanter(c) }, 'Not now'));
  const wrap = el('div', 'fixed inset-0 z-[65] flex items-center justify-center',
    { onClick: (e) => { if (e.target === wrap) closePlanter(c); } }, card);
  playSfx('ui_open');
  openOverlay(c, wrap);
}

/** One occupant line: portrait, name, and what they do here. */
function occupantRow(o: Occupant): HTMLElement {
  return el('div', 'flex items-center gap-2 py-1', {},
    el('img', 'w-7 h-7 object-contain shrink-0', { src: KIND_SPRITE[o.kind] ?? '', style: 'image-rendering:pixelated' }),
    el('div', 'min-w-0', {},
      el('div', 'text-[12px] truncate', {}, o.name),
      el('div', 'text-[11px] text-text-dim font-mono capitalize truncate', {}, o.detail)));
}

/**
 * "Who is in here" for a building that can hold anyone.
 *
 * The village state is fetched rather than read from the farm snapshot, because
 * the snapshot carries buildings but not villagers. The block renders
 * immediately with a placeholder and fills in when the answer lands: the panel's
 * buttons must never wait on a request, and an empty gap where the answer will
 * go reads as a bug. A building nobody is in says so in words — silence there
 * is indistinguishable from a failed load.
 */
function occupantsBlock(c: FarmCtx, b: Building): HTMLElement {
  const box = el('div', 'mb-3 pb-2 border-b border-border/60', {},
    el('div', 'text-[11px] uppercase tracking-wide text-text-dim mb-1', {}, 'Who is here'));
  const body = el('div', 'text-[12px] text-text-dim', {}, '…');
  box.append(body);

  void villageApi.get()
    .then((st) => {
      const occ = occupantsOf(b, st.villagers, c.farm?.buildings ?? []);
      body.replaceChildren();
      if (!occ.length) {
        body.append(el('div', 'text-[12px] text-text-dim', {},
          st.villagers.length ? 'Nobody yet — assign a villager from the Village panel.' : 'Nobody lives here — summon a villager first.'));
        return;
      }
      for (const o of occ) body.append(occupantRow(o));
    })
    .catch(() => { body.replaceChildren(el('div', 'text-[12px] text-text-dim', {}, 'Could not check who is here.')); });

  return box;
}

/**
 * What a tap on this building does — the whole decision, with no DOM in it.
 *
 * 'shop' is the Forager Hut once it is built: the hut IS the market, so the tap
 * goes straight to the counter rather than to a menu whose only trade row is one
 * more click away. Scaffolding still routes to the menu, which is where Finish-now
 * lives — there is no counter until the hut stands.
 */
export function buildingTapAction(b: Building): 'shop' | 'menu' {
  return b.kind === 'forage_hut' && b.isReady !== false ? 'shop' : 'menu';
}

/**
 * The hut's Move/Demolish, packaged for the shop panel to render in its footer.
 *
 * When the tap goes straight to the shop it skips this whole function, which is
 * the only place those two rows are built — so a hut opened that way became
 * permanently un-movable and un-demolishable. Handing them to the shop keeps the
 * trade one tap away AND keeps the building manageable; putting the player back
 * through an intermediate menu just to trade is what the tap-to-shop change
 * deliberately removed.
 */
export function foragerManage(c: FarmCtx, b: Building): ManageHooks {
  return {
    name: BUILDINGS[b.kind]?.name ?? b.kind,
    onMove: () => startMove(c, b),
    onDemolish: () => { closePlanter(c); confirmDemolish(b, () => demolish(c, b.tx, b.ty, b.kind, paintPlots)); },
  };
}

/**
 * The ONE thing this building's menu offers besides Move/Demolish.
 *
 * Pulled out of the render as a pure function so the routing can be tested — the
 * panel itself is DOM built inside a Pixi-driven view and cannot be exercised in
 * jsdom. Order matters and is preserved from the if-chain it replaced:
 * scaffolding always rushes first (nothing is usable until it stands).
 *
 * 'pens' is new. Before it, a finished coop or barn fell through every branch and
 * the tap offered Move and Demolish only, which is why the owner could not find
 * how to get a chicken into his coop — the buy flow existed, but its only door was
 * HUD → Village → Animals & pens.
 */
export type BuildingUse = 'rush' | 'furnace' | 'pens' | 'sleep' | 'shop' | 'bench' | null;

export function primaryBuildingAction(kind: string, unfinished: boolean): BuildingUse {
  if (unfinished) return 'rush';
  if (kind === 'furnace') return 'furnace';
  if (isPenBuilding(kind)) return 'pens';
  if (kind === 'hut') return 'sleep';
  if (kind === 'forage_hut') return 'shop';
  if (STATION_KINDS.has(kind)) return 'bench';
  return null;
}

export function openBuildingMenu(c: FarmCtx, b: Building): void {
  if (buildingTapAction(b) === 'shop') { closePlanter(c); openForagerShop(c, foragerManage(c, b)); return; }
  const def = BUILDINGS[b.kind];
  const name = def?.name ?? b.kind;
  const unfinished = isUnderConstruction(b);
  const remain = unfinished ? liveRemaining(c, b.remainingMs ?? 0) : 0;

  const row = (label: string, cls: string, onClick: () => void) =>
    el('button', `w-full px-3 py-2 mb-2 rounded border text-sm ${cls}`, { onClick }, label);

  const card = el('div', 'bg-surface border border-border rounded-lg p-4 w-[300px] max-w-[90vw] shadow-lift sheet-card', {},
    el('div', 'flex items-center gap-3 mb-3', {},
      el('img', 'w-10 h-10 object-contain', { src: `/sprites/buildings/${b.kind}.png`, style: 'image-rendering:pixelated' }),
      el('div', 'flex-1', {},
        el('h3', 'font-heading text-gold', {}, name),
        el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1', {},
          ...richText(unfinished ? `🔨 ${fmtCountdown(remain)} left` : `at ${b.tx}, ${b.ty}`)))));

  // Who lives/works here comes BEFORE the actions: it is what the click was for.
  // Skipped while the building is still scaffolding — nobody moves into a site.
  if (!unfinished && OCCUPIABLE_KINDS.has(b.kind)) card.append(occupantsBlock(c, b));

  const act = primaryBuildingAction(b.kind, unfinished);
  if (act === 'rush') {
    card.append(row('Finish now (Shrooms)', 'border-shroom text-shroom hover:bg-shroom/10',
      () => { closePlanter(c); openBuildRush(c, b); }));
  } else if (act === 'furnace') {
    card.append(row('Open the furnace', 'border-gold text-gold hover:bg-gold/10',
      () => { closePlanter(c); openSmelting(c); }));
    card.append(row('Crafting', 'border-border hover:border-gold hover:text-gold',
      () => { closePlanter(c); openCrafting(c); }));
  } else if (act === 'pens') {
    card.append(row('Animals & pens · buy stock', 'border-gold text-gold hover:bg-gold/10',
      // The tapped tile is passed as the purchase target so a chicken bought here
      // moves into THIS coop, not into whichever pen happens to sort first.
      () => { closePlanter(c); openHusbandry(c, { tx: b.tx, ty: b.ty }); }));
  } else if (act === 'sleep') {
    card.append(row('Sleep · a full night', 'border-gold text-gold hover:bg-gold/10',
      // Was a direct doSleep. Sleeping now costs four hours, so the click that
      // starts it has to say so before it happens, not after.
      () => openSleepPanel(c)));
  } else if (act === 'shop') {
    card.append(row('Trade with the forager', 'border-gold text-gold hover:bg-gold/10',
      () => { closePlanter(c); openForagerShop(c); }));
  } else if (act === 'bench') {
    card.append(row('Open the bench', 'border-gold text-gold hover:bg-gold/10',
      () => { closePlanter(c); openCrafting(c); }));
  }

  // Moving is free and keeps the build timer running (server: field/move), so it is
  // the safe alternative to the demolish-and-rebuild players otherwise attempt —
  // that pair charges the materials twice and throws the progress away.
  card.append(row('Move it', 'border-border hover:border-gold hover:text-gold', () => startMove(c, b)));
  card.append(row(`Demolish ${name}`, 'border-error text-error hover:bg-error/10',
    () => { closePlanter(c); confirmDemolish(b, () => demolish(c, b.tx, b.ty, b.kind, paintPlots)); }));
  card.append(el('button', 'w-full px-3 py-2 rounded border border-border text-text-dim hover:text-text text-sm',
    { onClick: () => closePlanter(c) }, 'Close'));

  const wrap = el('div', 'fixed inset-0 z-[65] flex items-center justify-center',
    { onClick: (e) => { if (e.target === wrap) closePlanter(c); } }, card);
  playSfx('ui_open');
  openOverlay(c, wrap);
}
